import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export default function Register() {
    const { register, user, error, setError } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { setError?.(""); }, [setError]);
    useEffect(() => { if (user && user.id) navigate("/", { replace: true }); }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const ok = await register(email, password, name);
        setBusy(false);
        if (ok) navigate("/", { replace: true });
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-ink-50">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-haevn-700 text-white relative overflow-hidden grain">
                <Logo tone="dark" className="h-7" showSub />
                <div className="max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-4">Onboarding</div>
                    <h1 className="font-editorial text-4xl xl:text-5xl leading-[1.05] mb-6">
                        A calm, structured<br />path to mastery.
                    </h1>
                    <p className="text-sm text-white/70 leading-relaxed">
                        Modules, flash cards, quizzes, and roleplay — all in one focused environment built for the HAEVN sales floor.
                    </p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-50 font-mono">v1.0 — Internal</div>
            </div>

            <div className="flex flex-col justify-center p-8 md:p-16">
                <div className="w-full max-w-sm mx-auto">
                    <div className="lg:hidden mb-8"><Logo tone="light" className="h-7" showSub /></div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Create account</div>
                    <h2 className="font-editorial text-3xl text-ink-900 mb-1">Request access.</h2>
                    <p className="text-sm text-ink-500 mb-8">The first account becomes the workspace administrator.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Full name</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="register-name-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700"
                                placeholder="Avery Reyes"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="register-email-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700"
                                placeholder="you@haevn.com"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="register-password-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700"
                                placeholder="At least 6 characters"
                            />
                        </div>
                        {error && (
                            <div className="text-xs text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="register-error">{error}</div>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="register-submit-btn"
                            className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                            {busy ? "Creating account…" : "Create account"}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-ink-200 text-center">
                        <span className="text-sm text-ink-500">Already have access? </span>
                        <Link to="/login" data-testid="register-to-login-link" className="text-sm text-haevn-700 hover:text-haevn-900 underline underline-offset-4 font-medium">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
